import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from '@store/slices/authSlice'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  Alert,
  CircularProgress,
  Skeleton
} from '@mui/material'
import {
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  Subscriptions as SubscriptionsIcon,
  Message as MessageIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts'

const Dashboard = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const token = useSelector(selectToken)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (token) {
      fetchDashboardData()
    } else {
      setError('Authentication token not found')
      setLoading(false)
    }
  }, [token])

  const fetchDashboardData = async () => {
    try {
      console.log('🔄 Fetching dashboard data...')
      setLoading(true)
      setError(null)

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/reports/dashboard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.')
        } else {
          throw new Error(`Server error: ${response.status}`)
        }
      }
      
      const result = await response.json()
      console.log('📊 Dashboard data received:', result)
      
      if (result.success) {
        setDashboardData(result.data)
        setError(null)
      } else {
        throw new Error(result.message || 'Failed to fetch dashboard data')
      }
    } catch (error) {
      console.error('❌ Failed to fetch dashboard data:', error)
      setError(error.message || 'Network error: Unable to fetch dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const kpiCards = [
    {
      title: t('dashboard.totalUsers'),
      value: dashboardData?.metrics?.totalUsers || 0,
      icon: PeopleIcon,
      color: 'primary.main',
      change: dashboardData?.percentageChanges?.users ? `+${dashboardData.percentageChanges.users}%` : '0%',
      changeType: 'positive'
    },
    {
      title: t('dashboard.activeSubscriptions'),
      value: dashboardData?.metrics?.activeSubscriptions || 0,
      icon: SubscriptionsIcon,
      color: 'secondary.main',
      change: dashboardData?.percentageChanges?.subscriptions ? `+${dashboardData.percentageChanges.subscriptions}%` : '0%',
      changeType: 'positive'
    },
    {
      title: t('dashboard.remindersToday'),
      value: dashboardData?.metrics?.todayReminders || 0,
      icon: NotificationsIcon,
      color: 'warning.main',
      change: dashboardData?.percentageChanges?.reminders ? `+${dashboardData.percentageChanges.reminders}%` : '0%',
      changeType: 'positive'
    },
    {
      title: t('dashboard.messagesSent'),
      value: dashboardData?.metrics?.messagesSent || 0,
      icon: MessageIcon,
      color: 'success.main',
      change: dashboardData?.percentageChanges?.messages ? `+${dashboardData.percentageChanges.messages}%` : '0%',
      changeType: 'positive'
    }
  ]

  const recentReminders = dashboardData?.recentActivity?.reminders || []

  const chartData = dashboardData?.charts?.dailyData || []

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('dashboard.title')}
        </Typography>
        
        {/* Loading KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" height={40} />
                  <Skeleton variant="text" width="30%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Loading Charts */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="rectangular" height={300} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rectangular" height={300} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('dashboard.title')}
        </Typography>
        
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Unable to load dashboard data
          </Typography>
          <button 
            onClick={fetchDashboardData}
            style={{
              padding: '8px 16px',
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        {t('dashboard.title')}
      </Typography>
      
      {/* Real-time success message */}
      {dashboardData && (
        <Alert severity="success" sx={{ mb: 3 }}>
          ✅ Dashboard loaded with real database data - Total Users: {dashboardData.metrics.totalUsers}, 
          Active Users: {dashboardData.metrics.activeUsers}, 
          Total Reminders: {dashboardData.metrics.totalReminders}
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        {kpiCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography color="text.secondary" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {card.title}
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                        {card.value.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={card.changeType === 'positive' ? 'success.main' : 'error.main'}
                        sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {card.change} {t('dashboard.fromLastMonth')}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: card.color,
                        borderRadius: '50%',
                        p: { xs: 0.75, sm: 1 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: { xs: 36, sm: 48 },
                        height: { xs: 36, sm: 48 }
                      }}
                    >
                      <card.icon sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.5rem' } }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Charts and Tables */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Daily Activity Chart */}
        <Grid item xs={12} md={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('Daily Activity')}
                </Typography>
                <Box sx={{ height: 300, mt: 2 }}>
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          fontSize={12}
                          tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis fontSize={12} />
                        <Tooltip 
                          labelFormatter={(value) => new Date(value).toLocaleDateString()}
                          formatter={(value, name) => [value, name === 'reminders' ? 'Reminders' : 'Users']}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="reminders" 
                          stroke={theme.palette.primary.main} 
                          strokeWidth={2}
                          dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="users" 
                          stroke={theme.palette.secondary.main} 
                          strokeWidth={2}
                          dot={{ fill: theme.palette.secondary.main, strokeWidth: 2, r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">No chart data available</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.recentReminders')}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {recentReminders.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('Users')}</TableCell>
                            <TableCell>{t('Status')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recentReminders.slice(0, 5).map((reminder, index) => (
                            <TableRow key={reminder.id || index}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {reminder.user || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {reminder.text?.substring(0, 30)}...
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={reminder.status || 'pending'}
                                  size="small"
                                  color={
                                    reminder.status === 'completed' ? 'success' : 
                                    reminder.status === 'pending' ? 'warning' : 
                                    'default'
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No recent reminders
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Dashboard